/**
 * <oppie-cart-upsell> rehydrator — safety net.
 *
 * Shopify's `/cart/add.js` and `/cart/change.js` endpoints sometimes return
 * a cached section render that doesn't include the upsell rail (typically
 * because the section was first cached with an empty cart, when the upsell
 * snippet's conditional skipped it). Even with the snippet now always
 * emitting the wrapper, certain edge cases (cold caches, stale dev servers)
 * can still leave the rail missing after a morph.
 *
 * This rehydrator listens for every cart update, and when the drawer no
 * longer has an upsell rail, refetches the section directly (which always
 * renders fresh) and splices the wrapper back in. Top-level listener so it
 * survives the morphs that destroy the upsell element itself.
 */

import { ThemeEvents } from '@theme/events';

const REHYDRATE_DEBOUNCE_MS = 350;

let debounceTimer = null;
let inFlight = false;

function findCartItemsContainer() {
  return document.querySelector('cart-drawer-component .cart-drawer__items');
}

function findExistingUpsell(container) {
  return container?.querySelector('.cart-drawer__upsell');
}

function findSectionId() {
  const el = document.querySelector('cart-items-component[data-drawer]');
  return el?.dataset.sectionId || null;
}

async function rehydrate() {
  if (inFlight) return;
  const container = findCartItemsContainer();
  if (!container) return;
  if (findExistingUpsell(container)) return;

  const sectionId = findSectionId();
  if (!sectionId) return;

  inFlight = true;
  try {
    const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}&_t=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) return;
    const data = await res.json();
    const sectionHTML = data[sectionId];
    if (!sectionHTML) return;

    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const freshUpsell = doc.querySelector('.cart-drawer__upsell');
    if (!freshUpsell) return;

    const currentContainer = findCartItemsContainer();
    if (!currentContainer) return;
    if (findExistingUpsell(currentContainer)) return;

    currentContainer.appendChild(freshUpsell.cloneNode(true));
  } catch {
    /* silent — upsell is non-critical */
  } finally {
    inFlight = false;
  }
}

function scheduleRehydrate() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(rehydrate, REHYDRATE_DEBOUNCE_MS);
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleRehydrate);
